import axios from "axios";

axios.defaults.withCredentials = true;

export const getAccessToken = () => {
  return localStorage.getItem("accessToken");
};

export const clearAuth = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken"); // remove old migration token if it exists
  localStorage.removeItem("user");
  localStorage.removeItem("device");
};

export const saveTokens = ({ access, user, device }) => {
  if (access) localStorage.setItem("accessToken", access);
  if (user) localStorage.setItem("user", JSON.stringify(user));
  if (device) localStorage.setItem("device", JSON.stringify(device));

  // Important: refresh token is now HttpOnly cookie, not localStorage.
  localStorage.removeItem("refreshToken");
};

const decodeJwt = (token) => {
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return null;

    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch (error) {
    return null;
  }
};

export const isTokenValid = (token) => {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false;

  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now + 10;
};

export const isAccessTokenValid = (token) => {
  return isTokenValid(token);
};

export const refreshAccessToken = async () => {
  try {
    const response = await axios.post(
      "/petgrooming/auth/token/refresh/",
      {},
      { withCredentials: true }
    );

    if (response.data.access) {
      localStorage.setItem("accessToken", response.data.access);

      if (response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }

      if (response.data.device) {
        localStorage.setItem("device", JSON.stringify(response.data.device));
      }

      localStorage.removeItem("refreshToken");
      return response.data.access;
    }

    clearAuth();
    return null;
  } catch (error) {
    clearAuth();
    return null;
  }
};

export const getValidAccessToken = async () => {
  const access = getAccessToken();

  if (access && isTokenValid(access)) {
    return access;
  }

  return await refreshAccessToken();
};

export const logout = async () => {
  try {
    const token = await getValidAccessToken();

    if (token) {
      await axios.post(
        "/petgrooming/auth/logout/",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
    }
  } catch (error) {
    // Even if backend logout fails, clear frontend session.
  }

  clearAuth();
  window.location.replace("/singin");
};

let refreshingPromise = null;

axios.interceptors.request.use(async (config) => {
  config.withCredentials = true;

  const url = String(config.url || "");

  const isAuthUrl =
    url.includes("/petgrooming/auth/email-login/start/") ||
    url.includes("/petgrooming/auth/email-login/verify/") ||
    url.includes("/petgrooming/auth/token/refresh/");

  if (isAuthUrl) {
    return config;
  }

  let token = getAccessToken();

  if (!token || !isTokenValid(token)) {
    if (!refreshingPromise) {
      refreshingPromise = refreshAccessToken().finally(() => {
        refreshingPromise = null;
      });
    }

    token = await refreshingPromise;
  }

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const url = String(originalRequest.url || "");

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !url.includes("/petgrooming/auth/")
    ) {
      originalRequest._retry = true;

      const newToken = await refreshAccessToken();

      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest.withCredentials = true;
        return axios(originalRequest);
      }
    }

    if (error.response && [401, 403].includes(error.response.status)) {
      clearAuth();
      window.location.replace("/singin");
    }

    return Promise.reject(error);
  }
);

const originalFetch = window.fetch.bind(window);

window.fetch = async (input, init = {}) => {
  const url = typeof input === "string" ? input : input.url || "";

  const isAuthUrl =
    url.includes("/petgrooming/auth/email-login/start/") ||
    url.includes("/petgrooming/auth/email-login/verify/") ||
    url.includes("/petgrooming/auth/token/refresh/");

  if (isAuthUrl) {
    return originalFetch(input, {
      ...init,
      credentials: "include",
    });
  }

  const token = await getValidAccessToken();

  const headers = new Headers(init.headers || {});

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return originalFetch(input, {
    ...init,
    credentials: "include",
    headers,
  });
};
